import { Component, OnInit, AfterViewInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { LibraryModule } from "../../app/shared/lib/lib.module";
import { Router } from "@angular/router";
import { pageLoadAnimation } from "../shared/utils/common-animations";
import {
  DailySummary,
  DailySummaryEvent,
  DailySummaryPlanDay,
  DailySummaryService,
} from "../services/daily-summary.service";
import { EventService } from "../services/event.service";
import { UserService } from "../core/services/user.service";
import { getUserDetails } from "../shared/utils/storage-utils";

@Component({
  selector: "success-component, SuccessComponent",
  standalone: true,
  templateUrl: './success.component.html',
  styleUrls: ['./success.component.css'],
  imports: [CommonModule, LibraryModule],
  animations: [pageLoadAnimation]
})
export class SuccessComponent implements OnInit, AfterViewInit {
  pageLoadState = 'hidden';
  activitiesSelected = false;
  isLoadingSummary = true;
  /** Only dates with a meal booking — one table row per day. */
  planDays: DailySummaryPlanDay[] = [];
  /** Days that have one or more booked activities (shown in Activities section). */
  activityDays: Array<{ dateLabel: string; activityNames: string[] }> = [];
  /** Registered / waitlisted special events. */
  registeredEvents: DailySummaryEvent[] = [];
  cancellingEventId: string | null = null;

  constructor(
    private readonly router: Router,
    private readonly dailySummaryService: DailySummaryService,
    private readonly eventService: EventService,
    private readonly userService: UserService
  ) {}

  ngOnInit(): void {
    const state = (history.state as { activitiesSelected?: boolean }) || {};
    this.activitiesSelected = state.activitiesSelected ?? false;
    this.loadSummary();
  }

  ngAfterViewInit(): void {
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.pageLoadState = 'visible';
      }, 50);
    });
  }

  /** Normalize date label (strip any legacy trailing period). */
  dateLabel(label: string | null | undefined): string {
    const trimmed = (label ?? '').trim();
    return trimmed.endsWith('.') ? trimmed.slice(0, -1) : trimmed;
  }

  /** Figma activities: comma-separated names, or fallback label. */
  activitiesDisplay(row: DailySummaryPlanDay): string {
    const names = (row.activityNames ?? [])
      .map((name) => (name ?? '').trim())
      .filter((name) => !!name);
    if (names.length > 0) {
      return names.join(', ');
    }
    return row.activitiesLabel || 'None';
  }

  mealDisplay(row: DailySummaryPlanDay): string {
    const label = (row.mealLabel ?? '').trim();
    if (label) {
      return label;
    }
    return row.mealScheduled ? 'Scheduled' : 'None';
  }

  eventStatusLabel(event: DailySummaryEvent): string {
    if (event.isWaitlisted) {
      const count = Number(event.waitlistedCount ?? 0);
      return count > 0 ? `Waiting List (${count})` : 'Waiting List';
    }
    return 'Registered';
  }

  onViewRegisteredEvent(event: DailySummaryEvent): void {
    const id = (event?.id ?? '').trim();
    if (!id) {
      return;
    }
    this.eventService.setSelectedEvent(id);
    void this.router.navigate(['/event-details'], {
      state: { activitiesSelected: this.activitiesSelected },
    });
  }

  onCancelRegisteredEvent(event: DailySummaryEvent): void {
    const id = (event?.id ?? '').trim();
    const userId = this.getUserId();
    if (!id || !userId || this.cancellingEventId === id) {
      return;
    }

    this.cancellingEventId = id;
    this.eventService.unregisterFromEvent(id, userId).subscribe({
      next: (response) => {
        this.cancellingEventId = null;
        if (response?.statusCode === 200 || response?.isRegistered === false) {
          this.registeredEvents = this.registeredEvents.filter((item) => item.id !== id);
        }
      },
      error: () => {
        this.cancellingEventId = null;
      },
    });
  }

  /**
   * Restart from the date-choose screen so the participant can edit meal,
   * transport, and activities from the beginning of the plan flow.
   */
  onEditClick(dateValue: string): void {
    if (!dateValue) {
      return;
    }
    sessionStorage.setItem('dateValue', dateValue);
    // Clear any prior edit short-circuit so meal → activity flow can continue.
    sessionStorage.removeItem('previousRoute');
    sessionStorage.setItem('editFromSuccess', 'true');
    this.router.navigate(['/success-meal-transport']);
  }

  onBackClick(): void {
    void this.router.navigate(['/upcoming-events'], {
      state: { activitiesSelected: this.activitiesSelected },
    });
  }

  private loadSummary(): void {
    const userId = this.getUserId();
    if (!userId) {
      this.isLoadingSummary = false;
      this.applySummary(null);
      return;
    }

    this.dailySummaryService.loadSummary(userId).subscribe({
      next: (summary) => {
        this.isLoadingSummary = false;
        this.applySummary(summary);
      },
      error: () => {
        this.isLoadingSummary = false;
        this.applySummary(null);
      },
    });
  }

  private getUserId(): number | null {
    let userId = this.userService.getStoredUserId();
    if (!userId) {
      const userDetails = getUserDetails();
      userId = userDetails?.value ?? userDetails?.id ?? userDetails?.userId;
    }
    const parsed = Number(userId);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  private applySummary(summary: DailySummary | null): void {
    // Only meal-booked dates — if one day is booked, show only that one row.
    this.planDays = (summary?.planDays ?? []).filter((day) => day.mealBooked === true);
    this.registeredEvents = summary?.events ?? [];
    this.activityDays = (summary?.planDays ?? [])
      .map((day) => {
        const names = (day.activityNames ?? [])
          .map((name) => (name ?? '').trim())
          .filter((name) => !!name);
        return {
          dateLabel: day.dateLabel,
          activityNames: names,
        };
      })
      .filter((day) => day.activityNames.length > 0);
  }

  onDoneClick(): void {
    this.router.navigate(['/card-checkin']);
  }
}
